import type { IAccountSettings } from './stores/types'
import type { IProductSettingsApiResponse, IProductSettings } from './types'
import type { IJAMSAuthResponse } from './types'
import { getJamsBackendApi } from '@/utils'

export const verifyJAMSToken = async (token: string): Promise<IJAMSAuthResponse | null> => {
  const endpoint = import.meta.env.VITE_JAMS_BACKEND_URL
  const path = `account/user/verify`

  const response = await fetch(`${endpoint}/${path}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })

  try {
    const responseJson: IJAMSAuthResponse = await response.json()

    if (!response.ok) {
      return null
    }

    return responseJson
  } catch (error) {
    return null
  }
}

export const verifyJAMSAdminToken = async (token: string): Promise<IJAMSAuthResponse | null> => {
  const endpoint = getJamsBackendApi()
  const path = `admin/verify`

  const response = await fetch(`${endpoint}/${path}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })

  try {
    const responseJson: IJAMSAuthResponse = await response.json()

    if (!response.ok) {
      return null
    }

    return responseJson
  } catch (error) {
    return null
  }
}

// Get account settings from JAMs
export const getAccountSettings = async (accountId: string): Promise<IAccountSettings | null> => {
  const endpoint = getJamsBackendApi()
  const path = `account?accountName=${accountId}`
  const response = await fetch(`${endpoint}/${path}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })

  try {
    const responseJson: any = await response.json()

    console.info('lightning_model_page', 'getAccountSettings', responseJson)

    if (!response.ok) {
      return null
    }

    return responseJson
  } catch (error) {
    console.error('lightning_model_page', 'getAccountSettings', error)
    return null
  }
}

// Get product settings from JAMs
export const getProductSettings = async (
  accountId: string,
  productId: string,
): Promise<IProductSettingsApiResponse | null> => {
  const endpoint = getJamsBackendApi()
  const path = `account/products/${accountId}/${productId}`

  console.info('lightning_model_page', 'getProductSettings', `${endpoint}/${path}`)

  try {
    const response = await fetch(`${endpoint}/${path}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      return null
    }

    const responseJson: any = await response.json()

    console.info('lightning_model_page', 'getProductSettings -- response', responseJson)

    return responseJson
  } catch (err) {
    console.error('lightning_model_page', 'getProductSettings', err)

    return null
  }
}

// Create or update product settings in JAMs
export const updateProductSettings = async (
  accountId: string,
  productId: string,
  settings: IProductSettings,
) => {
  const endpoint = getJamsBackendApi()
  const path = `account/products/${accountId}/${productId}`

  console.info('lightning_model_page', 'updateProductSettings', {
    request: `${endpoint}/${path}`,
    payload: {
      config: settings,
    },
  })

  try {
    const response = await fetch(`${endpoint}/${path}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        config: settings,
      }),
    })

    if (!response.ok) {
      return null
    }

    console.info('lightning_model_page', 'updateProductSettings', response)

    return response.ok
  } catch (error) {
    console.error(
      'lightning_model_page',
      'updateProductSettings',
      'Error updating product settings',
      error,
    )

    return null
  }
}

const CHROME_DATA_BASE_URL =
  import.meta.env.VITE_CHROME_DATA_API || 'https://d1nyh7pc35h4fw.cloudfront.net'

export interface Division {
  id: number
  name: string
}

export interface ModelItem {
  id: number
  name: string
}

export const fetchYearsFromChromeData = async (): Promise<Array<number> | null> => {
  try {
    const response = await fetch(`${CHROME_DATA_BASE_URL}/years`)

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status} - ${response.statusText}`)
    }

    const data = await response.json()

    if (!Array.isArray(data)) {
      return null
    }

    return data.map((y: number) => Number(y)).filter((y: number) => !Number.isNaN(y))
  } catch (error: unknown) {
    if (error instanceof TypeError) {
      console.error('Network error or CORS issue:', error.message)
    } else {
      console.error('An error occurred while fetching years:', (error as Error)?.message)
    }
    return null
  }
}

export const fetchMakesByYearFromChromeData = async (
  year: number,
): Promise<Array<string> | null> => {
  try {
    const response = await fetch(
      `${CHROME_DATA_BASE_URL}/divisions?modelYear=${encodeURIComponent(year)}`,
    )

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status} - ${response.statusText}`)
    }

    const data: Division[] = await response.json()

    if (!Array.isArray(data)) {
      return null
    }

    return data.map((d) => d.name)
  } catch (error: unknown) {
    if (error instanceof TypeError) {
      console.error('Network error or CORS issue:', error.message)
    } else {
      console.error('An error occurred while fetching makes:', (error as Error)?.message)
    }
    return null
  }
}

export const fetchModelsByYearAndMakeFromChromeData = async (
  year: number,
  make: string,
): Promise<Array<string> | null> => {
  try {
    const divisionsResponse = await fetch(
      `${CHROME_DATA_BASE_URL}/divisions?modelYear=${encodeURIComponent(year)}`,
    )

    if (!divisionsResponse.ok) {
      throw new Error(
        `HTTP error! Status: ${divisionsResponse.status} - ${divisionsResponse.statusText}`,
      )
    }

    const divisions: Division[] = await divisionsResponse.json()

    if (!Array.isArray(divisions)) {
      return null
    }

    const division = divisions.find((d) => d.name.toLowerCase() === make.toLowerCase())

    if (!division) {
      return []
    }

    const modelsResponse = await fetch(
      `${CHROME_DATA_BASE_URL}/models?modelYear=${encodeURIComponent(year)}&divisionId=${encodeURIComponent(division.id)}`,
    )

    if (!modelsResponse.ok) {
      throw new Error(
        `HTTP error! Status: ${modelsResponse.status} - ${modelsResponse.statusText}`,
      )
    }

    const models: ModelItem[] = await modelsResponse.json()

    if (!Array.isArray(models)) {
      return null
    }

    return models.map((m) => m.name)
  } catch (error: unknown) {
    if (error instanceof TypeError) {
      console.error('Network error or CORS issue:', error.message)
    } else {
      console.error('An error occurred while fetching models:', (error as Error)?.message)
    }
    return null
  }
}

export const logoutUser = async (token: string) => {
  const endpoint = getJamsBackendApi()
  const path = `user/logout`

  await fetch(`${endpoint}/${path}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
}
